"use client";

import { useMemo, useState, useEffect } from "react";
import styles from "@/app/(site)/components/news/news.module.css";
import SearchBar from "@/app/(site)/components/news/SearchBar";
import FeaturedList from "@/app/(site)/components/news/FeaturedList";
import Pagination from "@/app/(site)/components/news/Pagination";
import newsService, { News } from "@/services/newsService";
import Image from "next/image";
import Link from "next/link";

const LOGO_PATH = "/sdtc.webp";
const POPULAR_LOGO_PATH = "/sdtc.webp";

// Component để xử lý ảnh với fallback
function NewsThumbnail({ 
  src, 
  alt, 
  className, 
  width = 300, 
  height = 180 
}: { 
  src?: string; 
  alt: string; 
  className?: string;
  width?: number;
  height?: number;
}) {
  const [imgSrc, setImgSrc] = useState(src || LOGO_PATH);
  const [useFallback, setUseFallback] = useState(false);

  useEffect(() => {
    if (src) {
      setImgSrc(src);
      setUseFallback(false);
    }
  }, [src]);

  if (useFallback || !src) {
    return (
      <img
        src={LOGO_PATH}
        alt={alt}
        width={width}
        height={height}
        className={className}
        onError={(e) => {
          (e.currentTarget as HTMLImageElement).src = LOGO_PATH;
        }}
      />
    );
  }

  return (
    <Image
      src={imgSrc}
      alt={alt}
      width={width}
      height={height}
      className={className}
      unoptimized={imgSrc?.startsWith('http://localhost:7001') || 
                   imgSrc?.startsWith('http://127.0.0.1:7001') ||
                   imgSrc?.startsWith('http://192.168') ||
                   imgSrc?.startsWith('http://10.') ||
                   imgSrc?.startsWith('http://172.')}
      onError={() => {
        setUseFallback(true);
        setImgSrc(LOGO_PATH);
      }}
      onLoadingComplete={(result) => {
        if (result.naturalWidth === 0) {
          setUseFallback(true);
          setImgSrc(LOGO_PATH);
        }
      }}
    />
  );
}

// Component riêng cho phần Phổ biến sử dụng /sdtc.webppgit l làm fallback
function PopularThumbnail({ 
  src, 
  alt, 
  className, 
  width = 56, 
  height = 56 
}: { 
  src?: string; 
  alt: string; 
  className?: string;
  width?: number;
  height?: number;
}) {
  const [imgSrc, setImgSrc] = useState(src || POPULAR_LOGO_PATH);
  const [useFallback, setUseFallback] = useState(false);

  useEffect(() => {
    if (src) {
      setImgSrc(src);
      setUseFallback(false);
    }
  }, [src]);

  if (useFallback || !src) {
    return (
      <img
        src={POPULAR_LOGO_PATH}
        alt={alt}
        width={width}
        height={height}
        className={className}
        onError={(e) => {
          (e.currentTarget as HTMLImageElement).src = POPULAR_LOGO_PATH;
        }}
      />
    );
  }

  return (
    <Image
      src={imgSrc}
      alt={alt}
      width={width}
      height={height}
      className={className}
      unoptimized={imgSrc?.startsWith('http://localhost:7001') || 
                   imgSrc?.startsWith('http://127.0.0.1:7001') ||
                   imgSrc?.startsWith('http://192.168') ||
                   imgSrc?.startsWith('http://10.') ||
                   imgSrc?.startsWith('http://172.')}
      onError={() => {
        setUseFallback(true);
        setImgSrc(POPULAR_LOGO_PATH);
      }}
      onLoadingComplete={(result) => {
        if (result.naturalWidth === 0) {
          setUseFallback(true);
          setImgSrc(POPULAR_LOGO_PATH);
        }
      }}
    />
  );
}

function slugify(text: string): string {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

export default function NewsPage() {
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [newsData, setNewsData] = useState<News[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(1);

  const perPage = 6;

  useEffect(() => {
    const fetchNews = async () => {
      try {
        setLoading(true);
        const response = await newsService.getNews(
          {
            page,
            limit: perPage,
            sortBy: "publishedDate",
            order: "desc",
          },
          { forcePublic: true }
        );
        setNewsData(response.data);
        setTotalPages(response.pagination.totalPages);
      } catch (error) {
        console.error("Lỗi khi tải tin tức:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchNews();
  }, [page]);

  const categories = useMemo(() => {
    const setCats = new Set<string>();
    newsData.forEach((m) => m.type && setCats.add(m.type));
    return ["Tất cả", ...Array.from(setCats)];
  }, [newsData]);

  const featured = useMemo(() => {
    return newsData.slice(0, 3).map((news) => ({
      _id: news.id.toString(),
      slug: news.slug,
      title: news.title,
      summary: news.summary,
      image: news.imageUrl || news.image,
      category: news.category?.name || news.type,
      author: news.author,
      status: news.status as "published" | "draft",
      publishedDate: new Date(news.publishedDate).toLocaleDateString("vi-VN"),
      views: news.views,
    }));
  }, [newsData]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return newsData;
    return newsData.filter((m) =>
      (m.title + " " + (m.summary || "")).toLowerCase().includes(q)
    );
  }, [query, newsData]);

  const pageItems = filtered;

  return (
    <div className={styles.container}>
      {/* ====== HEADER CÓ VIDEO NỀN ====== */}
      <header className={styles.header}>
        <video
          className={styles.headerVideo}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          poster="/sdtc.webp"
        >
          <source src="/video/bannertintuc.mp4" type="video/mp4" />
        </video>
        <div className={styles.headerOverlay}></div>
        <SearchBar
          onSearch={(q) => {
            setQuery(q);
            setPage(1);
          }}
        />
      </header>

      {/* ====== TIN MỚI NHẤT ====== */}
      <section className={styles.newSection} style={{ marginTop: 24 }}>
        <h3 style={{ marginBottom: 16 }}>Tin mới nhất</h3>
        {loading ? (
          <div style={{ textAlign: "center", padding: "40px" }}>
            <p>Đang tải tin tức...</p>
          </div>
        ) : pageItems.length === 0 ? (
          <div style={{ textAlign: "center", padding: "40px" }}>
            <p>Không tìm thấy tin tức nào.</p>
          </div>
        ) : (
          <div className={styles.newGrid}>
            {pageItems.map((n) => {
              const newsSlug = n.slug || slugify(n.title);
              return (
                <article key={n.id} className={styles.newCard}>
                  <Link
                    href={`/tin-tuc/${newsSlug}`}
                    className={styles.newThumbLink}
                  >
                    <NewsThumbnail
                      src={n.imageUrl || n.image}
                      alt={n.title}
                      className={styles.newThumb}
                    />
                  </Link>
                  <div className={styles.newContent}>
                    <Link
                      href={`/tin-tuc/${newsSlug}`}
                      className={styles.newTitleLink}
                    >
                      <h4 className={styles.newTitle}>{n.title}</h4>
                    </Link>
                    <div className={styles.newMeta}>
                      <span>
                        {new Date(n.publishedDate).toLocaleDateString("vi-VN")}
                      </span>
                      {n.category?.name || n.type ? (
                        <span>• {n.category?.name || n.type}</span>
                      ) : null}
                      <span>• {n.author || "Ban biên tập"}</span>
                    </div>
                    <p style={{ marginTop: 8, color: "#6b7280" }}>
                      {n.summary}
                    </p>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>

      {/* ====== PAGINATION ====== */}
      <Pagination
        currentPage={page}
        totalPages={totalPages}
        onPageChange={(p) => {
          setPage(p);
          window.scrollTo({ top: 120, behavior: "smooth" });
        }}
      />

      {/* ====== KHÁM PHÁ / VIDEO ====== */}
      <section className={styles.promoSection}>
        <div className={styles.promoIntro}>
          <h2 className={styles.promoTitle}>
            NHẬN BACKLINK MIỄN PHÍ TỪ DIỄN ĐÀN 247
          </h2>
          <p className={styles.promoDesc}>
            Đăng ký ngay để nhận{" "}
            <strong>1 backlink dofollow chất lượng</strong> từ{" "}
            <a
              href="https://diendan247.com/"
              target="_blank"
              rel="noopener noreferrer"
              className={styles.link}
            >
              Diễn đàn 247
            </a>
            .
          </p>
          <a
            href="https://diendan247.com/"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.exploreBtn}
          >
            KHÁM PHÁ NGAY
          </a>
        </div>

        <div className={styles.promoVideo}>
          <video
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            poster="/sdtc.webp"
          >
            <source src="/video/mau.mp4" type="video/mp4" />
          </video>
        </div>
      </section>

      {/* ====== PHỔ BIẾN ====== */}
      <section className={styles.popularSection}>
        <h3 style={{ marginBottom: 18, color: "#111827" }}>Phổ biến</h3>
        <div className={styles.popularGrid}>
          {newsData.slice(3, 7).map((p) => {
            const newsSlug = p.slug || slugify(p.title);
            return (
              <Link
                key={p.id}
                href={`/tin-tuc/${newsSlug}`}
                className={styles.popularLink}
              >
                <div className={styles.popularCard}>
                  <PopularThumbnail
                    src={p.imageUrl || p.image}
                    alt={p.title}
                    className={styles.popularThumb}
                    width={56}
                    height={56}
                  />
                  <div className={styles.popularText}>
                    <div className={styles.popularTitle}>{p.title}</div>
                    <div className={styles.popularMeta}>
                      <span>
                        {new Date(p.publishedDate).toLocaleDateString("vi-VN")}
                      </span>
                      <span>• {p.author || "Ban biên tập"}</span>
                    </div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* ====== FEATURED ====== */}
      <FeaturedList items={featured} />
    </div>
  );
}
